import { CDN_ROOT, getCdnImageUrl } from "../features/images";
import { getGenderKey } from "../features/utils";
import { Gender } from "./gender";
import { Physiology } from "./physiology";
import { FunctionFactory } from "survey-core";

enum BodyType {
  Skinny = 'Skinny',
  SkinnyFat = 'Skinny Fat',
  Defined = 'Defined',
  Athletic = 'Athletic',
  Bulky = 'Bulky',
  Overweight = 'Overweight',
}

const bodyTypeQuestion = {
  name: "bodytype",
  title: `What best matches your <div class="highlight">current body</div> type?`,
  description: `No right or wrong – we use this to better understand your starting point.`,
  descriptionLocation: 'underTitle',
  type: "imagepicker",
  showLabel: true,
  isRequired: true,
};

const bodyTypeMaleQuestion = {
  ...bodyTypeQuestion,
  visibleIf: `getGender({gender},{physiology}) = "${Gender.Male}"`,
  requiredIf: `getGender({gender},{physiology}) = "${Gender.Male}"`,
  choices: [
    {
      "value": BodyType.Skinny,
      "imageLink": `${CDN_ROOT}/images/bodytype/Male/skinny.png`,
      "text": "Skinny",
    },
    {
      "value": BodyType.SkinnyFat,
      "imageLink": `${CDN_ROOT}/images/bodytype/Male/skinnyfat.png`,
      "text": "Skinny Fat",
    },
    {
      "value": BodyType.Bulky,
      "imageLink": `${CDN_ROOT}/images/bodytype/Male/bulky.jpg`,
      "text": "Bulky",
    },
    {
      "value": BodyType.Overweight,
      "imageLink": `${CDN_ROOT}/images/bodytype/Male/overweight.jpg`,
      "text": "Overweight",
    },
  ]
};

const bodyTypeFemaleQuestion = {
  ...bodyTypeQuestion,
  visibleIf: `getGender({gender},{physiology}) = "${Gender.Female}"`,
  requiredIf: `getGender({gender},{physiology}) = "${Gender.Female}"`,
  choices: [
    {
      "value": BodyType.Skinny,
      "imageLink": `${CDN_ROOT}/images/bodytype/Female/skinny.jpg`,
      "text": "Skinny",
    },
    {
      "value": BodyType.SkinnyFat,
      "imageLink": `${CDN_ROOT}/images/bodytype/Female/skinnyfat.jpg`,
      "text": "Skinny Fat",
    },
    {
      "value": BodyType.Bulky,
      "imageLink": `${CDN_ROOT}/images/bodytype/Female/bulky.png`,
      "text": "Bulky",
    },
    {
      "value": BodyType.Overweight,
      "imageLink": `${CDN_ROOT}/images/bodytype/Female/overweight.jpg`,
      "text": "Overweight",
    },
  ]
};


const bodyTypeBothQuestion = {
  ...bodyTypeQuestion,
  
  choices: [
    {
      "value": BodyType.Skinny,
      "imageLink": `${CDN_ROOT}/images/bodytype/Male/skinny.png`,
      "text": "Skinny",
      visibleIf:`getGender({gender},{physiology}) = "${Gender.Male}"`,
    },
    {
      "value": BodyType.SkinnyFat,
      "imageLink": `${CDN_ROOT}/images/bodytype/Male/skinnyfat.png`,
      "text": "Skinny Fat",
      visibleIf:`getGender({gender},{physiology}) = "${Gender.Male}"`,
    },
    {
      "value": BodyType.Bulky,
      "imageLink": `${CDN_ROOT}/images/bodytype/Male/bulky.jpg`,
      "text": "Bulky",
      visibleIf:`getGender({gender},{physiology}) = "${Gender.Male}"`,
    },
    {
      "value": BodyType.Overweight,
      "imageLink": `${CDN_ROOT}/images/bodytype/Male/overweight.jpg`,
      "text": "Overweight",
      visibleIf:`getGender({gender},{physiology}) = "${Gender.Male}"`,
    },
    {
      "value": BodyType.Skinny,
      "imageLink": `${CDN_ROOT}/images/bodytype/Female/skinny.jpg`,
      "text": "Skinny",
      visibleIf:`getGender({gender},{physiology}) = "${Gender.Female}"`,
    },
    {
      "value": BodyType.SkinnyFat,
      "imageLink": `${CDN_ROOT}/images/bodytype/Female/skinnyfat.jpg`,
      "text": "Skinny Fat",
      visibleIf:`getGender({gender},{physiology}) = "${Gender.Female}"`,
    },
    {
      "value": BodyType.Bulky,
      "imageLink": `${CDN_ROOT}/images/bodytype/Female/bulky.png`,
      "text": "Bulky",
      visibleIf:`getGender({gender},{physiology}) = "${Gender.Female}"`,
    },
    {
      "value": BodyType.Overweight,
      "imageLink": `${CDN_ROOT}/images/bodytype/Female/overweight.jpg`,
      "text": "Overweight",
      visibleIf:`getGender({gender},{physiology}) = "${Gender.Female}"`,
    },
  ]

};

const bodyTypeCarouselQuestion = {
  name: "bodytype",
  title: `What best matches your <div class="highlight">current body</div> type?`,
  description: "No right or wrong – we use this to better understand your starting point.",
  descriptionLocation: 'underTitle',
  type: "carousel",
  isRequired: true,
  choices: [
    {
      value: BodyType.Skinny,
      text: "Slender",
      imageUrl: `${CDN_ROOT}/images/new-quiz-bodytype-images-2025/male-bodytype/1.png`,
      description: "Small frame, low muscle & body fat",
      visibleIf: `getGender({gender},{physiology}) = ${Gender.Male}`,
    },
    {
      value: BodyType.SkinnyFat,
      text: "Skinny Fat",
      imageUrl: `${CDN_ROOT}/images/new-quiz-bodytype-images-2025/male-bodytype/2.png`,
      description: "Slim frame, softer mid-section",
      visibleIf: `getGender({gender},{physiology}) = ${Gender.Male}`,
    },
    {
      value: BodyType.Defined,
      text: "Defined",
      imageUrl: `${CDN_ROOT}/images/new-quiz-bodytype-images-2025/male-bodytype/3.png`,
      description: "Lean, tight and toned",
      visibleIf: `getGender({gender},{physiology}) = ${Gender.Male}`,
    },
    {
      value: BodyType.Athletic,
      text: "Athletic",
      imageUrl: `${CDN_ROOT}/images/new-quiz-bodytype-images-2025/male-bodytype/4.png`,
      description: "Balanced size and musculature",
      visibleIf: `getGender({gender},{physiology}) = ${Gender.Male}`,
    },
    {
      value: BodyType.Bulky,
      text: "Bulky",
      imageUrl: `${CDN_ROOT}/images/new-quiz-bodytype-images-2025/male-bodytype/5.png`,
      description: "Heavier set, low muscle definition",
      visibleIf: `getGender({gender},{physiology}) = ${Gender.Male}`,
    },
    {
      value: BodyType.Overweight,
      text: "Soft & Heavy",
      imageUrl: `${CDN_ROOT}/images/new-quiz-bodytype-images-2025/male-bodytype/6.png`,
      description: "Broad frame, higher body fat",
      visibleIf: `getGender({gender},{physiology}) = ${Gender.Male}`,
    },


    {
      value: BodyType.Skinny,
      text: "Slender",
      imageUrl: `${CDN_ROOT}/images/new-quiz-bodytype-images-2025/female-bodytype/1.png`,
      description: "Narrow frame with low body fat",
      visibleIf: `getGender({gender},{physiology}) = ${Gender.Female}`,
    },
    {
      value: BodyType.SkinnyFat,
      text: "Skinny Fat",
      imageUrl: `${CDN_ROOT}/images/new-quiz-bodytype-images-2025/female-bodytype/2.png`,
      description: "Slim frame, soft features",
      visibleIf: `getGender({gender},{physiology}) = ${Gender.Female}`,
    },
    {
      value: BodyType.Defined,
      text: "Toned",
      imageUrl: `${CDN_ROOT}/images/new-quiz-bodytype-images-2025/female-bodytype/3.png`,
      description: "Slim, firm, and defined",
      visibleIf: `getGender({gender},{physiology}) = ${Gender.Female}`,
    },
    {
      value: BodyType.Athletic,
      text: "Strong & Balanced",
      imageUrl: `${CDN_ROOT}/images/new-quiz-bodytype-images-2025/female-bodytype/4.png`,
      description: "Proportional and visibly strong",
      visibleIf: `getGender({gender},{physiology}) = ${Gender.Female}`,
    },
    {
      value: BodyType.Bulky,
      text: "Solid & Curvy",
      imageUrl: getCdnImageUrl("images/new-quiz-bodytype-images-2025/female-bodytype/5.png"),
      description: "Wider frame and lower tone",
      visibleIf: `getGender({gender},{physiology}) = ${Gender.Female}`,
    },
    {
      value: BodyType.Overweight,
      text: "Soft & Heavy",
      imageUrl: `${CDN_ROOT}/images/new-quiz-bodytype-images-2025/female-bodytype/6.png`,
      description: "Larger frame, higher body fat",
      visibleIf: `getGender({gender},{physiology}) = ${Gender.Female}`,
    },
  ]
};

function getGender(params: any[]): string {
  if (params === undefined || params === null) return "";
  const gender = params[0];
  const physiology = params[1];
  return getGenderKey(gender, physiology);
}

FunctionFactory.Instance.register('getGender', getGender);


export {
  BodyType,
  bodyTypeMaleQuestion,
  bodyTypeFemaleQuestion,
  bodyTypeBothQuestion,
  bodyTypeCarouselQuestion
}
